import { useEffect, useState } from "react";
import { Modal } from "antd";
import Button from "../button";

const CONSENT_KEY = "ualign_cookie_consent";

type ConsentState = {
  necessary: true;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: string;
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div style={{ marginBottom: "20px" }}>
    <h3
      style={{
        fontFamily: "var(--font-family-quicksand)",
        fontWeight: 600,
        fontSize: "14px",
        color: "#ea5383",
        marginBottom: "6px",
      }}
    >
      {title}
    </h3>
    <div
      style={{
        fontFamily: "var(--font-family-quicksand)",
        fontSize: "13px",
        color: "#444",
        lineHeight: 1.7,
      }}
    >
      {children}
    </div>
  </div>
);

const CookiePolicyContent = () => (
  <div>
    <Section title="1. About this Cookie Policy">
      This Cookie Policy explains how UALIGN LTD uses cookies and similar technologies on our
      website, dentist portal, patient app, QR code pages, and related digital services. This
      policy should be read together with our Privacy Policy and Terms of Use.
    </Section>

    <Section title="2. What are cookies?">
      Cookies are small text files placed on your device when you visit a website or use an online
      service. They help websites work properly, remember choices, improve user experience,
      understand how services are used, and support security or marketing functions. Similar
      technologies may include pixels, tags, scripts, local storage, software development kits, and
      device identifiers.
    </Section>

    <Section title="3. Why we use cookies">
      UALIGN may use cookies and similar technologies to make our website and portal work correctly;
      keep users logged in securely; protect accounts and patient data; remember user preferences;
      understand how visitors use our website; improve our website, app, and services; measure
      marketing performance; and support communication, booking, forms, or app download links.
    </Section>

    <Section title="4. Types of cookies we use">
      <p style={{ marginBottom: "8px" }}>
        <strong>Strictly necessary</strong> — Required for the website, portal, app, or QR code
        pages to function. Used for security, login, session management, fraud prevention, load
        balancing, and remembering privacy choices. These cannot be switched off.
      </p>
      <p style={{ marginBottom: "8px" }}>
        <strong>Functional</strong> — Help remember choices and improve your experience, such as
        language, display preferences, region, or saved settings. Consent required where not
        strictly necessary.
      </p>
      <p style={{ marginBottom: "8px" }}>
        <strong>Analytics</strong> — Help us understand how visitors use our website or app (pages
        visited, time spent, links clicked, errors). Not essential. Used only after consent.
      </p>
      <p style={{ marginBottom: "8px" }}>
        <strong>Marketing</strong> — Used to measure advertising performance and show more relevant
        advertisements. Used only with your consent.
      </p>
      <p style={{ marginBottom: 0 }}>
        <strong>Third-party</strong> — May be placed by analytics providers, payment providers,
        video players, map services, chat tools, app store links, social media platforms, or
        advertising partners.
      </p>
    </Section>

    <Section title="5. Cookies we may use">
      The exact cookies used may change depending on the website features, app features, analytics
      tools, and marketing tools active at the time. Example entries may include login session
      cookies, cookie consent preference cookies, Google Analytics, Meta Pixel, app or website
      performance tools, and error tracking tools.
    </Section>

    <Section title="6. Consent">
      When you first visit our website, we show a cookie banner. You can choose whether to accept
      or reject non-essential cookies. Non-essential cookies include analytics, marketing, and some
      functional cookies. Strictly necessary cookies may be used without consent. You can change
      your cookie choices at any time by using the cookie settings link on our website.
    </Section>

    <Section title="7. How to manage cookies">
      You can manage cookies by using the cookie banner, changing your browser settings to block or
      delete cookies, using private browsing, changing privacy settings on your mobile device, or
      using opt-out tools from analytics or advertising providers. If you block all cookies, some
      parts of the UALIGN portal may not work properly.
    </Section>

    <Section title="8. Patient app and QR code tracking">
      UALIGN may use QR codes and app-related technologies to help patients access app downloads,
      register their case, receive aligner instructions, and follow treatment reminders. We may
      collect technical information such as device type, app version, operating system, scan or
      access time, and error logs. Where this relates to a patient case or treatment, it is handled
      according to our Privacy Policy and applicable data protection law.
    </Section>

    <Section title="9. Dentist portal cookies">
      The UALIGN dentist portal may use cookies and similar technologies for secure login, case
      upload sessions, user authentication, account protection, portal settings, error logs, and
      service improvement. Because the dentist portal may involve patient data, some cookies and
      technical logs are necessary for security and service operation.
    </Section>

    <Section title="10. Updates to this Cookie Policy">
      We may update this Cookie Policy from time to time to reflect changes in our website, app,
      portal, technology, legal obligations, or cookie providers. The latest version will be
      published on our website with the effective date.
    </Section>

    <Section title="11. Contact">
      <div>UALIGN LTD</div>
      <div>Company number: 17062684 — Registered in England and Wales</div>
      <div>128, City Road, London, EC1V 2NX, United Kingdom</div>
      <div>
        Email:{" "}
        <a href="mailto:info@ualign.co.uk" style={{ color: "#ea5383" }}>
          info@ualign.co.uk
        </a>
      </div>
      <div>Phone: +44 7537 135002</div>
      <div>
        Website:{" "}
        <a
          href="https://ualign.co.uk"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#ea5383" }}
        >
          ualign.co.uk
        </a>
      </div>
    </Section>
  </div>
);

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem(CONSENT_KEY)) {
      setVisible(true);
    }
  }, []);

  const save = (functional: boolean, analytics: boolean, marketing: boolean) => {
    const consent: ConsentState = {
      necessary: true,
      functional,
      analytics,
      marketing,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    setVisible(false);
  };

  return (
    <>
      {visible && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            backgroundColor: "#fff",
            boxShadow: "0 -4px 24px rgba(0,0,0,0.08), 0 -1px 6px rgba(0,0,0,0.04)",
            borderTop: "3px solid #ea5383",
            padding: "16px 32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "24px",
            flexWrap: "wrap",
          }}
        >
          <p
            style={{
              margin: 0,
              flex: 1,
              minWidth: "260px",
              fontSize: "13px",
              color: "#444",
              lineHeight: 1.65,
              fontFamily: "var(--font-family-quicksand)",
              fontWeight: 500,
            }}
          >
            We use cookies to keep you securely logged in, protect patient data, and improve our
            portal. Strictly necessary cookies are always active. By clicking{" "}
            <strong>Accept All</strong> you also consent to functional and analytics cookies.{" "}
            <button
              onClick={() => setPolicyOpen(true)}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                color: "#ea5383",
                textDecoration: "underline",
                fontWeight: 600,
                fontFamily: "var(--font-family-quicksand)",
                fontSize: "13px",
                cursor: "pointer",
              }}
            >
              Cookie Policy
            </button>
          </p>
          <div style={{ display: "flex", gap: "10px", flexShrink: 0, flexWrap: "wrap" }}>
            <Button type="default" onClick={() => save(false, false, false)}>
              Reject Non-Essential
            </Button>
            <Button type="primary" onClick={() => save(true, true, true)}>
              Accept All
            </Button>
          </div>
        </div>
      )}

      <Modal
        open={policyOpen}
        onCancel={() => setPolicyOpen(false)}
        footer={
          <Button type="primary" onClick={() => setPolicyOpen(false)}>
            Close
          </Button>
        }
        title={
          <span
            style={{
              fontFamily: "var(--font-family-quicksand)",
              fontWeight: 600,
              fontSize: "16px",
            }}
          >
            Cookie Policy
          </span>
        }
        width={680}
        styles={{ body: { maxHeight: "60vh", overflowY: "auto", paddingTop: "16px" } }}
      >
        <CookiePolicyContent />
      </Modal>
    </>
  );
};

export default CookieBanner;
